<template>
  <div class="tabs">
    <div
      v-show="activeLayout === 1 && tab.open"
      v-for="tab in tabs"
      :key="tab.name"
      class="tab navTab"
      :class="{ 'selected-tab': activeTab.name === tab.name }"
      @click="selectTab(tab)"
    >
      {{ $tc(tab.display, 2) }}
      <span
        v-if="tab.name !== 'home'"
        @click.stop="removeTab(tab)"
        class="tab-close-button"
      />
    </div>

    <div
      v-show="tabCreator && activeLayout === 1"
      class="tab-create-container"
      v-on-clickaway="hideTabMenu"
    >
      <v-btn
        text
        height="40"
        width="40"
        class="open-tab-button ma-0"
        :disabled="disabledMenuButton"
        @click="toggleTabMenu"
      >
        <v-icon size="17" color="#FFF" style="line-height: 0;"> add_circle</v-icon>
      </v-btn>

      <div class="tab-menu" v-show="tabMenuOpen">
        <div
          v-for="tab in tabs"
          :key="tab.name"
          v-if="tab.name !== 'home' && (isSuperAdmin || !tab.dev)"
          class="tab-menu-option"
          @click.stop="createTab(tab)"
          :class="{
            'disabled-tab-button': tab.open || tab.disabled
          }"
        >
          {{ $tc(tab.display, 2) }}
          <dev-tag
            v-if="tab.dev"
            position="relative"
            :style="{ zoom: '80%', 'margin-left': '20px' }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { bus } from "@/main.js";
import { mixin as clickaway } from "vue-clickaway";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";

import AddTabSVG from "@/assets/home/tabs/addTab.svg";
import DevTag from "@/components/_Atoms/DevTag";

import HomeTab from "../Content/MapT";
import NetworkTab from "../Content/Tabs/Network/NetworkTab";
import ReportsTab from "../Content/Tabs/Reports/ReportsTab";
import ValidationTab from "../Content/Tabs/Validations/ValidationsTab";
import ActivitiesTab from "../Content/Tabs/Activities/ActivitiesTab";
import InsidePlan from "../Content/Tabs/InsidePlan/InsidePlan";

export default {
  name: "HomeTabsManagement",

  components: {
    AddTabSVG,
    DevTag
  },

  mixins: [clickaway, checkPermissionsMixin],

  data() {
    return {
      /** List of tabs that might be opened in the Fibercloud home */
      tabs: [
        {
          name: "home",
          display: "OutsidePlan",
          component: HomeTab,
          open: true,
          permission: undefined,
          disabled: false,
          dev: false
        },
        {
          name: "network",
          display: "Network",
          component: NetworkTab,
          open: false,
          permission: {
            permissionName: "network_draw",
            role: "fibercloud_web"
          },
          disabled: false,
          dev: false
        },
        {
          name: "validation",
          display: "QualityControl",
          component: ValidationTab,
          open: false,
          permission: {
            permissionName: "validation_tab_show",
            role: "fibercloud_web"
          },
          disabled: false,
          dev: false
        },
        {
          name: "reports",
          display: "Report",
          component: ReportsTab,
          open: false,
          permission: {
            permissionName: "export_list",
            role: "management"
          },
          disabled: false,
          dev: false
        },
        {
          name: "insidePlan",
          display: "InsidePlan",
          component: InsidePlan,
          open: false,
          permission: undefined,
          disabled: true,
          dev: false
        },
        {
          name: "activities",
          display: "Activity",
          component: ActivitiesTab,
          open: false,
          // permission: {
          //   permissionName: "field_activity",
          //   role: "field_management"
          // },
          permission: undefined,
          disabled: false,
          dev: true
        }
      ],
      /** Object containing the info of the currently active tab */
      activeTab: {},
      /**
       * Creates an array of objects corresponding to the open tabs.
       * @returns {array}
       */
      openTabs: [],
      /** Defines if the tab creator button is enabled or not */
      tabCreator: true,
      /** Defines if the tab tab menu is open or not */
      tabMenuOpen: false,
      /** Currently active layout mode or number of tabs opened side by side. */
      activeLayout: 1,

      disabledMenuButton: true
    };
  },

  computed: {
    tabOpenedElsewhere() {
      return this.$store.getters.getActiveFibercloudTab;
    }
  },

  mounted() {
    this.selectTab(this.tabs[0]);
    this.updateOpenTabs();
    bus.$on("updateActiveLayout", newValue => {
      this.activeLayout = newValue;
    });
  },

  updated() {
    this.addPermissionsToTabs();
  },

  methods: {
    /**
     * Changes the active tab to the one clicked on.
     * @param {object} tab - Tab to show
     */
    selectTab(tab) {
      this.activeTab = tab;
      bus.$emit("updateActiveTab", tab);
      this.$store.dispatch("SET_ACTIVE_FIBERCLOUD_TAB", tab.name);
      if (this.activeTab.name === this.tabs[0].name) {
        bus.$emit("enable-sidebar");
      } else {
        bus.$emit("disable-sidebar");
      }
    },
    /**
     * Creates/opens the selected tab.
     * @param {object} tab - Tab to open
     */
    createTab(tab) {
      if (!tab.open) {
        tab.open = true;
        this.selectTab(tab);
        this.updateOpenTabs();
        this.hideTabMenu();
      }
    },

    /**
     * Removes the tab from the list of open tabs
     * @param {object} tab - Tab to open
     */
    removeTab(tab) {
      tab.open = false;
      this.updateOpenTabs();
      if (this.activeTab.name === tab.name) {
        this.selectTab(this.tabs[0]);
      }
    },

    updateOpenTabs() {
      let openTabs = [];
      this.tabs.forEach(tab => {
        if (tab.open) {
          openTabs.push(tab);
        }
      });
      this.tabCreator = openTabs.length < this.tabs.length;
      bus.$emit("updateOpenTabs", openTabs);
      this.openTabs = openTabs;
    },
    /** Toggles menu **/
    toggleTabMenu() {
      this.tabMenuOpen = !this.tabMenuOpen;
    },
    /** Hides the new tab menu when clicking outside of it **/
    hideTabMenu() {
      this.tabMenuOpen = false;
    },
    /**
     * Updates the disabled value of each tab object with the respective values.
     */
    addPermissionsToTabs() {
      let nrOfTabOptions = this.tabs.length;
      for (let i = 1; i < nrOfTabOptions; i++) {
        let tab = this.tabs[i];
        if (tab.permission !== undefined) {
          this.tabs[i].disabled = !this.userHasPermission(
            tab.permission.permissionName,
            tab.permission.role
          );
        }
      }
      this.disabledMenuButton = false;
    }
  },

  watch: {
    tabOpenedElsewhere(newTabName) {
      let newTab = this.tabs.find(tab => tab.name === newTabName);
      if (!newTab.open) {
        this.createTab(newTab);
      } else {
        this.selectTab(newTab);
      }
    }
  },

  destroyed() {
    bus.$off("updateActiveLayout");
  }
};
</script>

<style scoped>
.tabs {
  width: 100%;
  height: 100%;
  overflow: visible;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
}

.tab {
  width: 215px;
  transition: 0.1s;
  min-width: 95px;
  height: 40px;
  position: relative;
  background-color: rgba(41, 189, 240, .5);
  color: var(--main-white);
  line-height: 33px;
  cursor: pointer;
  padding: 2px 25px 2px 8px;
  border: 1px solid var(--secondary-normal);
  word-break: break-all;
  overflow: hidden;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  border-radius: 4px;
  text-align: start;
}

.navTab:hover {
  background-color: rgb(41, 189, 240);
}

.selected-tab {
  background-color: rgb(41, 189, 240) !important;
  color: white;
  overflow: visible;
  transition: 0.1s;
  z-index: 1;
}

.tab-close-button {
  width: 10px;
  height: 15px;
  border-radius: 50%;
  position: absolute;
  top: 7px;
  cursor: pointer;
  right: 14px;
  transition: 0.1s;
}

.tab-close-button:after {
  content: "\D7";
  font-size: 20px;
  position: relative;
  top: -6px;
}

.tab-close-button:hover {
  background-color: unset;
}

.selected-tab > .tab-close-button:hover {
  background-color: unset;
}

.tab-create-container {
  width: 40px;
  height: 40px;
}

.tab-create-container .v-btn:before,
.tab-create-container .v-btn:after {
  background-color: unset;
}

.tab-menu {
  width: 115px;
  height: fit-content;
  background: white;
  color: black;
  padding: 2px 0;
  font-size: 10px;
  cursor: initial;
  text-align: left;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  margin-top: 2px;
}

.tab-menu-option {
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  margin: 3px;
  padding: 2px 10px;
}

.tab-menu-option:hover {
  color: var(--primary-highlight);
}
.disabled-tab-button {
  cursor: initial;
  color: #b7b7b7 !important;
  pointer-events: none;
}

.open-tab-button {
  background-color: rgb(41, 189, 240);
  min-width: 40px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/NavBar/TabsManagement.vue